import { CatalogCategory, CatalogCompany } from './catalog';
import { VerticalName } from './category';

export enum SupplierProfileTab {
  CONTACT = 'contact',
  CAPABILITIES = 'capabilities',
  REVIEWS = 'reviews',
  PROFILE_INFO = 'profile',
  BASE_PRODUCTS = 'products',
}

export interface IAttachment {
  fileName: string;
  fileUrl: string;
}

export interface SupplierProfileInstructionalVideo {
  vimeoId: number;
  thumbnailImage: string;
  title: string;
  timeLength: string;
}

export interface SupplierProfilePdfListItem {
  title: string;
  image: string;
}

export enum PDPContentTab {
  PRODUCT_DESCRIPTION = 'PRODUCT_DESCRIPTION',
  SUPPLIER_CAPABILITIES = 'SUPPLIER_CAPABILITIES',
  SIMILAR_PRODUCTS = 'SIMILAR_PRODUCTS',
  REVIEWS = 'REVIEWS',
}

export enum SupplierProfileProductsSort {
  NEW = 'new',
  FEATURED = 'featured',
}

export declare type CustomizationType = 'text' | 'colors' | 'tags' | 'upload_image' | 'image_opts';

export interface ICustomization {
  identifier: string;
  title: string;
  value: any;
  type: CustomizationType;
  exampleImages?: string | string[];
}

export interface IColor {
  identifier: string;
  name: string;
  colorVariant: string;
  hexCode: string;
}

export interface IOption {
  identifier?: string;
  name?: string;
  image?: string;
  color?: string;
  value?: string;
}

export interface ISourcingFilterOption {
  label: string;
  value: any;
  apiParmas?: any;
  isLeaf?: boolean;
  children?: IOption[];
}

export enum SourcingHomeTab {
  ALL = 'All',
  PRODUCTS = 'Products',
  PACKAGING = 'Ecommerce_Packaging',
  BUSINESS_AND_CREATIVE_SERVICES = 'Business_&_Creative_Services',
  WEBSITE_DEVELOPMENT = 'Website_Development',
}

export enum ViewType {
  SUPPLIERS = 'SUPPLIERS',
  CATALOG_PRODUCTS = 'CATALOG_PRODUCTS',
  RFP = 'RFP',
}

export interface HomeCard {
  preheader: string;
  header: string;
  factoryCount?: number;
  logos?: string[];
}

export const SamplePriceFilterOptions: ISourcingFilterOption[] = [
  {
    label: 'All',
    value: null,
    apiParmas: null,
  },
  {
    label: '$5-$50',
    value: '5,50',
    apiParmas: { minSamplePrice: 5, maxSamplePrice: 50 },
  },
  {
    label: '$50-$100',
    value: '50,100',
    apiParmas: { minSamplePrice: 50, maxSamplePrice: 100 },
  },
  {
    label: '$100+',
    value: '100',
    apiParmas: { minSamplePrice: 100 },
  },
  {
    label: 'Custom quote (price tbd)',
    value: 'custom',
    apiParmas: { nullSamplePrice: true },
  },
];

export const MoqFilterOptions: ISourcingFilterOption[] = [
  {
    label: 'All',
    value: null,
    apiParmas: null,
  },
  {
    label: '0-100 units',
    value: '0,100',
    apiParmas: { minMOQ: 0, maxMOQ: 100 },
  },
  {
    label: '100-1000 units',
    value: '100,1000',
    apiParmas: { minMOQ: 100, maxMOQ: 1000 },
  },
  {
    label: '1000+ units',
    value: '1000',
    apiParmas: { minSamplePrice: 1000 },
  },
  {
    label: 'Custom quote (MOQ tbd)',
    value: 'custom',
    apiParmas: { nullMOQ: true },
  },
];

export const AverageReviewFilterOptions: ISourcingFilterOption[] = [
  {
    label: 'All',
    value: null,
    apiParmas: null,
  },
  {
    label: '4 or up',
    value: '8',
    apiParmas: { companyReviewScore: 8 },
  },
  {
    label: '3 or up',
    value: '6',
    apiParmas: { companyReviewScore: 6 },
  },
  {
    label: '2 or up',
    value: '4',
    apiParmas: { companyReviewScore: 4 },
  },
  {
    label: '1 or up',
    value: '2',
    apiParmas: { companyReviewScore: 2 },
  },
];

export const ProductionTimeFilterOptions: ISourcingFilterOption[] = [
  {
    label: 'All',
    value: null,
    apiParmas: null,
  },
  {
    label: 'Less than 1 month',
    value: '1',
    apiParmas: { productionEtaFrom: 0, productionEtaTo: 30 },
  },
  {
    label: '1 to 2 months',
    value: '1,2',
    apiParmas: { productionEtaFrom: 30, productionEtaTo: 60 },
  },
  {
    label: '2 to 3 months',
    value: '2,3',
    apiParmas: { productionEtaFrom: 60, productionEtaTo: 90 },
  },
  {
    label: '3 months or higher',
    value: '3',
    apiParmas: { productionEtaFrom: 90 },
  },
];

export const OrderVolumeFilterOptions: ISourcingFilterOption[] = [
  {
    label: 'All',
    value: null,
    apiParmas: null,
  },
  {
    label: '250+',
    value: 'from250',
    apiParmas: { orderVolumeFrom: 250 },
  },
  {
    label: '100+',
    value: 'from100',
    apiParmas: { orderVolumeFrom: 100 },
  },
  {
    label: '50+',
    value: 'from50',
    apiParmas: { orderVolumeFrom: 50 },
  },
  {
    label: 'Less than 50',
    value: 'to50',
    apiParmas: { orderVolumeFrom: 0, orderVolumeTo: 50 },
  },
];

export interface AutocompleteCompany {
  id: number;
  avatarUrl: string;
  name: string;
  category?: {
    vertical: VerticalName;
    group: string;
    subgroup: string;
  };
}

export enum SourcingMarketplaceSearchType {
  HOMEPAGE = 'HOMEPAGE',
  BROWSE_MARKETPLACE = 'BROWSE_MARKETPLACE',
}

export interface SubcategoryMap {
  [verticalName: string]: { [groupName: string]: { [subgroupName: string]: CatalogCategory } };
}

export enum PackagingTab {
  GET_IN_8_10_DAYS = 'Get in 8-10 days',
  LOW_COST_OPTIONS = 'Low cost options',
  BY_CATEGORY = 'By category',
}

export interface SourcingMarketplaceBannerValueProp {
  icon: string;
  title: string;
  description: string;
  classname: string;
}

export interface SourcingBreadcrumb {
  type: 'L1' | 'L2' | 'supplierName' | 'productName';
  label: string;
  L1Key?: string; // when type is L1, need to set this value
  L2Key?: string; // when type is L2, need to set this value
  supplierId?: string; // when type is supplierName, need to set this value
}

export const getAvgProductionTime = (supplier: CatalogCompany) => {
  const eta = supplier?.productionEta;
  if (eta) {
    if (eta < 30) {
      return '<30 days';
    } else if (eta < 60) {
      return '30+ days';
    } else if (eta < 90) {
      return '60+ days';
    } else if (eta < 120) {
      return '90+ days';
    } else {
    }
  } else {
    return '120+ days';
  }
};

export const getAnnualCustomers = (supplier: CatalogCompany) => {
  if (supplier.annualCustomers < 50) {
    return null;
  } else if (supplier.annualCustomers < 100) {
    return '50+';
  } else if (supplier.annualCustomers < 250) {
    return '100+';
  } else {
    return '250+';
  }
};

export const getAnnualOrders = (supplier: CatalogCompany) => {
  if (supplier.annualOrders < 10) {
    return null;
  } else if (supplier.annualOrders < 25) {
    return '10+';
  } else if (supplier.annualOrders < 50) {
    return '25+';
  } else if (supplier.annualOrders < 100) {
    return '50+';
  } else if (supplier.annualOrders < 250) {
    return '100+';
  } else if (supplier.annualOrders < 500) {
    return '250+';
  } else {
    return '500+';
  }
};

export const getAnnualSales = (supplier: CatalogCompany) => {
  if (supplier.annualSales < 500) {
    return null;
  } else if (supplier.annualSales < 1000) {
    return '$500+';
  } else if (supplier.annualSales < 5000) {
    return '$1k+';
  } else if (supplier.annualSales < 10000) {
    return '$5k+';
  } else if (supplier.annualSales < 50000) {
    return '$10k+';
  } else if (supplier.annualSales < 100000) {
    return '$50k+';
  } else if (supplier.annualSales < 250000) {
    return '$100k+';
  } else {
    return '$250k+';
  }
};
